import {
  type FC,
  type MouseEvent,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import TetherComponent from "react-tether";

import { Button, getHashCode, Icon, UIContext } from "@alpacahq/ui/old";

import { SidebarItem, type SidebarItemProps } from "./SidebarItem";

export interface SidebarSettingsProps {
  settings: SidebarItemProps[];
}

export const SidebarSettings: FC<SidebarSettingsProps> = ({ settings }) => {
  const { setIsSidebarBlurred } = useContext(UIContext);

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpacity, setDropdownOpacity] = useState(0);
  const [forceUpdate, setForceUpdate] = useState(false);

  useEffect(() => {
    // blur the sidebar when the dropdown is open to focus on the dropdown
    setIsSidebarBlurred(isOpen);
  }, [isOpen, setIsSidebarBlurred]);

  useEffect(() => {
    // close the dropdown when clicking outside of it
    // granted it may be a click on the dropdown itself, but
    // we want to close it anyway when you click an item
    const handleClickOutside = () => setIsOpen(false);
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // due to positioning issues with the dropdown, we need to force a re-render after a delay
  // the dropdown will not get cut-off from parent scroll containers this way
  const toggleOpen = (event: MouseEvent) => {
    event.stopPropagation();

    setIsOpen(!isOpen);
    setDropdownOpacity(0);

    if (!isOpen) {
      setTimeout(() => {
        setForceUpdate(true);
        setDropdownOpacity(1);
      }, 100);
    }
  };

  const handleUpdate = useCallback(() => {
    // force a re-render after the first update
    if (forceUpdate) {
      setForceUpdate(false);
    }
  }, [forceUpdate]);

  return (
    <TetherComponent
      attachment="bottom left"
      targetAttachment="top left"
      offset="12px 0"
      onUpdate={handleUpdate}
      // @ts-expect-error @types/react-tether describes v0.5's children API and
      // declares no renderTarget, so passing v3's render prop is TS2769 and its
      // ref an implicit any (TS7006). v3 ships correct types in
      // lib/react-tether.d.ts that this tsconfig's resolution does not pick up.
      renderTarget={(ref) => (
        <Button
          ref={ref}
          variant="secondary"
          onClick={toggleOpen}
          aria-label="User profile options"
        >
          <Icon name="Cog6Tooth" />
        </Button>
      )}
      renderElement={(ref: RefObject<HTMLDivElement>) =>
        isOpen && (
          <div
            ref={ref}
            style={{ opacity: dropdownOpacity }}
            className="z-50 min-w-[186.4px] space-y-0.5 rounded-md border border-gray-200 bg-white p-1"
          >
            {settings.map((item) => (
              <SidebarItem key={getHashCode(item)} {...item} unresponsive />
            ))}
          </div>
        )
      }
    />
  );
};
